<template>
  <form class="form-login jumpstart-form" @submit.prevent="">
    <div class="login-merge-form__input">
      <LoginFormMessaging v-if="mergeData.error"
                          id="mergeErrorNotification"
                          :error-title="mergeError.title"
                          :error-message="mergeError.message"
                          @login-form-sign-out="processLogout"
                          @login-form-forgot-credentials="$emit('open-forgot-password')" />
      <fieldset class="login-merge-form__fieldset wk-js2">
        <UtdTextInput class="utd-text-input" 
                      input-id="mergeUsernameInput" 
                      :label="$t('LOGIN.USER_NAME')">
          <input id="mergeUsernameInput"
                 v-model="userName"
                 class="wk-field-input"
                 type="text"
                 autocomplete="username"
                 :aria-label="$t('LOGIN.USER_NAME')"
                 maxlength="50"
                 aria-required="true"
                 @input="onUsernameInput">
        </UtdTextInput>
        <div v-if="!showPasswordSection" class="login-merge-form__help">
          <UtdButton class="login-merge-form__help-link"
                     button-style="link"
                     button-icon="question-circle"
                     button-icon-position="right"
                     type="button"
                     @click="$emit('open-forgot-password')">
            <span>Forgot username</span>
          </UtdButton>
        </div>
        <div v-if="showPasswordSection" class="login-merge-form__password-section">
          <UtdPasswordInput :label="$t('LOGIN.PASSWORD')"
                            @text-input-toggle-show="toggleVisibleInput">
            <input id="mergePasswordInput"
                   ref="mergePasswordInputRef"
                   v-model="password"
                   :maxlength="maxPasswordFieldLength"
                   autocomplete="current-password"
                   :type="inputType"
                   class="wk-field-input"
                   :aria-label="$t('LOGIN.PASSWORD')"
                   @input="onPasswordInput">
          </UtdPasswordInput>
          <div class="login-merge-form__help">
            <UtdButton class="login-merge-form__help-link"
                       button-style="link"
                       button-icon="question-circle"
                       button-icon-position="right"
                       type="button"
                       @click="$emit('open-forgot-password')">
              <span>Forgot username or password</span>
            </UtdButton>
          </div>
        </div>
        <div class="login-merge-form__form-submit">
          <UtdButton v-if="!showPasswordSection" 
                     id="checkUsernameBtn"
                     :disabled="isContinueDisabled"
                     button-size="large"
                     @click="processUsername">
            <span v-show="isSubmitting" id="continueBtnPleaseWaitText">
              {{ $t('PLEASE_WAIT') }}</span>
            <span v-show="!isSubmitting" id="continueBtnContinueText">
              {{ $t('LOGIN.CONTINUE') }}</span>
          </UtdButton>
          <p v-if="showSsoMergeWarning"
             class="login-merge-form__username-flow-info">
            <b>After you merge accounts,</b> you will no longer sign in with
            an UpToDate username and password. The sign-in page will help
            you navigate to your organization's Single Sign-On (SSO) process.
          </p>
          <UtdButton v-if="showPasswordSection" 
                     id="mergeAccountsBtn"
                     :disabled="isFindAccountDisabled"
                     button-size="large"
                     @click="$emit('find-account')">
            <span v-show="isSubmitting" id="continueBtnPleaseWaitText">
              {{ $t('PLEASE_WAIT') }}</span>
            <span v-show="!isSubmitting" id="continueBtnContinueText">
              {{ $t('LOGIN.FIND_ACCOUNT') }}</span>
          </UtdButton>
        </div>
        <div v-if="props.showSearchableInstitutions">
          <div role="separator"
               class="institutions-separator"
               aria-label="SSO sign-in options">or</div>
          <SearchableInstitutionsSection class="searchable-institutions-section"
                                         :is-merging="true"
                                         @fetched-autocomplete="$emit('fetched-autocomplete')"
                                         @show-spinner="$emit('navigate-to-sso', $event)" />
        </div>

      </fieldset>
    </div>
  </form>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import {
    maxPasswordFieldLength,
    C_REG_MERGE,
    C_LOGIN_WORKFLOW
} from '_acaSrc/utility/constants';
import UtdTextInput from '_acaSrc/components/shared/input/UtdTextInput.vue';
import UtdPasswordInput from '_acaSrc/components/shared/input/UtdPasswordInput.vue';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import LoginFormMessaging from '_acaSrc/components/login/LoginFormMessaging.vue';
import useMergeAccounts from '_acaSrc/composables/mergeAccounts.composable.js';
import { getDocument, getWindow } from '_acaSrc/utility/DOM';
import { setQueryParamValue } from '_acaSrc/utility/http';
import SearchableInstitutionsSection
    from '_acaSrc/components/login/SearchableInstitutionsSection.vue';
import { SET_SHOULD_REDIRECT_POST_SSO } from '_acaSrc/store/login.store';

const props = defineProps({
    showSearchableInstitutions: {
        type: Boolean,
        default: true
    },
    mergeSource: {
        type: String,
        default: C_REG_MERGE.REGISTRATION_MERGE_TYPE
    }
});

const store = useStore();

const showPasswordSection = ref(false);
const userShouldHaveMergeWarning = ref(false);

const emit = defineEmits([
    'open-forgot-password',
    'find-account',
    'fetched-autocomplete',
    'navigate-to-sso'
]);

const processUsername = async() => {
    if (isSubmitting.value) {
        return;
    }
    setSubmitting(true);
    const response = await store.dispatch('login/getLoginWorkflow', { 
        username: userName.value,
        forceServerCall: props.mergeSource === C_REG_MERGE.REGISTRATION_MERGE_TYPE
    });
    userShouldHaveMergeWarning.value = response.mergeWarning;
    if (response.workflow === C_LOGIN_WORKFLOW.PROMPT_USER_FOR_PASSWORD
    || response.workflow === C_LOGIN_WORKFLOW.SHOW_NEXT_STEPS_FOR_SIGN_IN
    || !props.showSearchableInstitutions
    ) {
        showPasswordSection.value = true;
        nextTick(() => {
            const pwdElem = getDocument().querySelector('#mergePasswordInput');
            pwdElem?.focus();
        });
        setSubmitting(false);
        return;
    }
    handleSsoFlow(response);
};

const handleSsoFlow = (response) => {
    if (response.workflow === C_LOGIN_WORKFLOW.REDIRECT_TO_SSO && response.url) {
        setSubmitting(true);
        const url = `${response.url}?login_hint=${userName.value}`;
        const mergeRedirectUrl = setQueryParamValue(
            url, C_REG_MERGE.REG_MERGE_SOURCE, props.mergeSource
        );
        store.commit(`login/${SET_SHOULD_REDIRECT_POST_SSO}`, true);
        getWindow().location.href = mergeRedirectUrl;
    }
};

const isContinueDisabled = computed(() => 
    userName.value.trim() === '' 
    || mergeData.error);
const isFindAccountDisabled = computed(() => 
    userName.value.trim() === '' 
    || password.value.trim() === '' 
    || mergeData.error);
const inputType = ref('password');
const toggleVisibleInput = (hideText) => {
    inputType.value = hideText ? 'password' : 'text';
};

const showSsoMergeWarning = computed(() =>
    props.mergeSource === C_REG_MERGE.REGISTRATION_MERGE_TYPE &&
    showPasswordSection.value &&
    userShouldHaveMergeWarning.value);

const onUsernameInput = () => {
    mergeData.error = false;
    showPasswordSection.value = false;
    password.value = '';
};

const onPasswordInput = () => {
    mergeData.error = false;
};

const processLogout = async() => {
    try {
        await utdRest('logout');
        emit('open-forgot-password');
    }
    catch {
        // No-op
    }
};

const {
    userName,
    password,
    mergeError,
    mergeData,
    isSubmitting,
    setSubmitting
} = useMergeAccounts();
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles.less';

.login-merge-form__form-submit {
  display: flex;
  flex-direction: column;
}

#checkUsernameBtn, #mergeAccountsBtn {
  flex: 1;
}

.login-merge-form__password-section {
  .ds1-mt-4();

  :deep(.wk-field-password) {
    .ds1-mb-0();
  }
}

.login-merge-form__fieldset {
  .ds1-pa-0();
  border: none;

  .utd-text-input {
    .ds1-mb-0();
  }
}

.login-merge-form__input {
  .ds1-mb-3();

  .wk-field-input {
    background-color: @DS1-UTD-PRIMARY-BG-COLOR;
  }
}

.login-merge-form__help {
  .ds1-mb-2();
  display: flex;
  justify-content: flex-end;
  font-size: 14px;
}

.login-merge-form__help-link {
  .ds1-pa-0();
  white-space: nowrap;
}

#mergeErrorNotification {
  .ds1-mb-3();
}

.institutions-separator {
  .wk-options-separator();
  .ds1-mv-4();
}

.login-merge-form__username-flow-info {
  .ds1-mb-2();
}
</style>
